/**
 * Available state types for the Status component
 */
export type State =
  | 'Action Required'
  | 'Added'
  | 'AI'
  | 'All'
  | 'Blocked'
  | 'Cleared'
  | 'Completed'
  | 'Downloaded'
  | 'Draft'
  | 'Error'
  | 'Future'
  | 'Help'
  | 'In Progress'
  | 'Incomplete'
  | 'Information'
  | 'Loading'
  | 'Not Started'
  | 'Not Started - Open'
  | 'Pending'
  | 'Ready for Review'
  | 'Removed'
  | 'Revisit'
  | 'Shared'
  | 'Shared Open'
  | 'Step Complete'
  | 'Warning';

/**
 * Available status icon types
 */
export type StatusIcon =
  | 'Action Required'
  | 'Added'
  | 'AI'
  | 'All'
  | 'Blocked'
  | 'Cleared'
  | 'Completed'
  | 'Downloaded'
  | 'Draft'
  | 'Error'
  | 'Future'
  | 'Help'
  | 'In Progress'
  | 'Incomplete'
  | 'Information'
  | 'Loading'
  | 'Not Started'
  | 'Not Started - Open'
  | 'Pending'
  | 'Ready for Review'
  | 'Removed'
  | 'Revisit'
  | 'Shared'
  | 'Shared Open'
  | 'Step Complete'
  | 'Warning';

/**
 * Available icon type categories
 */
export type IconType = 'Nav Status' | 'Folder Closed' | 'Folder Open' | 'Status' | 'Status - Big';

/**
 * Component size type
 */
export type Size = 'Regular' | 'Small';

/**
 * Get the CSS class name for a given state or icon
 * @param stateOrIcon - State or icon name
 * @returns CSS class name
 */
export const getStatusIconClass = (stateOrIcon: State | StatusIcon | string): string => {
  const map: Record<string, string> = {
    'Action Required': 'warning',
    AI: 'ai-task',
    Added: 'complete',
    All: 'information',
    Blocked: 'error',
    Cleared: 'cleared',
    Completed: 'complete',
    'Step Complete': 'complete',
    Downloaded: 'download',
    Draft: 'draft',
    Error: 'error',
    Future: 'future',
    Help: 'information',
    'In Progress': 'in-progress',
    Incomplete: 'incomplete',
    Information: 'information',
    Loading: 'loading',
    'Not Started': 'not-started',
    'Not Started - Open': 'not-started',
    Pending: 'pending',
    'Ready for Review': 'ready',
    Removed: 'error',
    Revisit: 'revisit',
    Shared: 'shared',
    'Shared Open': 'shared',
    Warning: 'warning',
  };

  return map[stateOrIcon] || 'in-progress';
};

/**
 * Get the CSS class for the icon size based on icon type
 * @param iconType - Icon type category
 * @returns CSS class name for icon size
 */
export const getIconSizeClass = (iconType: IconType): string => {
  return iconType === 'Status - Big' ? 'icon-big' : '';
};

/**
 * Get all available states for Storybook controls
 * @returns Array of all available states
 */
export const getAllStates = (): State[] => {
  return [
    'Action Required',
    'Added',
    'AI',
    'All',
    'Blocked',
    'Cleared',
    'Completed',
    'Downloaded',
    'Draft',
    'Error',
    'Future',
    'Help',
    'In Progress',
    'Incomplete',
    'Information',
    'Loading',
    'Not Started',
    'Not Started - Open',
    'Pending',
    'Ready for Review',
    'Removed',
    'Revisit',
    'Shared',
    'Shared Open',
    'Step Complete',
    'Warning',
  ];
};

/**
 * Get all available status icons for Storybook controls
 * @returns Array of all available status icons
 */
export const getAllStatusIcons = (): StatusIcon[] => {
  return [
    'Action Required',
    'Added',
    'AI',
    'All',
    'Blocked',
    'Cleared',
    'Completed',
    'Downloaded',
    'Draft',
    'Error',
    'Future',
    'Help',
    'In Progress',
    'Incomplete',
    'Information',
    'Loading',
    'Not Started',
    'Not Started - Open',
    'Pending',
    'Ready for Review',
    'Removed',
    'Revisit',
    'Shared',
    'Shared Open',
    'Step Complete',
    'Warning',
  ];
};

/**
 * Get all available icon types for Storybook controls
 * @returns Array of all available icon types
 */
export const getAllIconTypes = (): IconType[] => {
  return ['Nav Status', 'Folder Closed', 'Folder Open', 'Status', 'Status - Big'];
};

